import * as AR from '@dora/arctic';
import { connectToParent, AsyncMethodReturns } from 'penpal';
import * as H from 'preact/hooks';
import { AppConfig } from '../../data/config';
import * as Logging from '../../store/actions/logging';
import * as Core from '../../store/core';
import { Core as CoreSelectors } from '../../store/selectors';
import { useDispatch, useSelector } from './redux';
import * as ConsoleCommands from './useConsoleCommand';

export type View = {
    name: string;
    data: any | undefined;
};

export type Cart = {
    conversionId: string;
    products: Array<CartLineItem>;
    price?: number; // Optional for Inspiration
};

export type CartLineItem = {
    id: string;
    quantity: number;
};

export type CheckoutLocation = 'finalise step' | 'ar' | 'model viewer';

// TODO: this should be exposed by the parent package somehow
export interface ParentAPI {
    closeWidget: () => void;
    onCheckout: (data: Cart, checkoutLocation: CheckoutLocation) => void;
    trackEvent: (data: { event: string; properties?: { [key: string]: any } }) => void;
    toggleModal: (modal: boolean) => void;
    conversionIdChanged: (conversionId: string) => void;
    listCommandNames: (commands: string[]) => void;
}

/**
 * Set up the API for communicating with the parent of the iFrame this app is in
 */
export function useParentAPI() {
    // initialise app
    const view = useSelector(CoreSelectors.view);
    const currentUrl = useSelector(CoreSelectors.currentUrl);

    const dispatch = useDispatch();

    /**
     * A series of functions which are all called when the app is closed. We use a record to enable
     * the functions to be 'named' and therefore overwritten.
     */
    const closeAppActionsR = H.useRef<Record<string, () => void>>({});
    const [proceedToCart, setProceedToCart] = H.useState<
        undefined | (() => (_cart: Cart, _from: CheckoutLocation) => void)
    >(undefined);
    const [trackEvent, setTrackEvent] = H.useState(
        () => (_: string, __?: { [key: string]: any }) => {},
    );
    const [toggleModal, setToggleModal] = H.useState(() => (_: boolean) => {});
    const [updateConversionId, setUpdateConversionId] = H.useState(() => (_: string) => {});

    H.useEffect(() => {
        // we can't have this outside the useEffect as a useState as the effect would re-run every
        // time it changed.
        let parent: undefined | AsyncMethodReturns<ParentAPI>;

        const connection = connectToParent<ParentAPI>({
            methods: {
                booststrap(data: AppConfig) {
                    dispatch(Logging.logDebug('[child] Received bootstrap message', data));
                    dispatch(Core.bootstrap(data));
                },
                startInspirationRunway(
                    productId: string,
                    placement: AR.Placement,
                    shouldOpenArModal: boolean,
                    dimensionsVisibleInViewer: boolean,
                    token: string,
                    modalText: AR.ModalText,
                    currentUrl: string,
                ) {
                    const data = {
                        productId,
                        shouldOpenArModal,
                        placement,
                        dimensionsVisibleInViewer,
                        token,
                        modalText,
                        currentUrl,
                    };
                    dispatch(
                        Logging.logDebug('[child] recieved Start Inspiration Runway message', data),
                    );
                    dispatch(Core.setOpen());
                    dispatch(Core.startInspirationRunway({ ...data }));
                },
                startConfiguration(
                    configuratorId: string,
                    currentUrl: string,
                    startWithSelections?: Record<string, string | number | null>,
                ) {
                    dispatch(
                        Logging.logDebug('[child] Received startConfiguration message', {
                            configurationId: configuratorId,
                            currentUrl,
                            startWithSelections,
                        }),
                    );
                    dispatch(Core.setOpen());
                    dispatch(
                        Core.startConfiguration({
                            configuratorId,
                            startWithSelections: startWithSelections ?? {},
                            currentUrl,
                        }),
                    );
                },
                startConfigurationFromSave(
                    savedConfigurationId: string,
                    currentUrl: string,
                    ar: boolean,
                ) {
                    dispatch(
                        Logging.logDebug('[child] Received startConfigurationFromSave message', {
                            savedConfigurationId,
                            currentUrl,
                            ar,
                        }),
                    );
                    dispatch(Core.setOpen());
                    dispatch(
                        Core.startConfigurationFromSave({
                            currentUrl: currentUrl,
                            configurationId: savedConfigurationId,
                            ar: ar,
                        }),
                    );
                },
                startSpace(productId: string, currentUrl: string) {
                    dispatch(
                        Logging.logDebug('[child] Received startSpace message', {
                            productId,
                            currentUrl,
                        }),
                    );
                    dispatch(Core.setOpen());
                    dispatch(
                        Core.startSpaceFromSku({
                            currentUrl: currentUrl,
                            sku: productId,
                        }),
                    );
                },
                startSpaceFromLayout(layoutId: string, currentUrl: string, ar: boolean) {
                    dispatch(
                        Logging.logDebug('[child] Received startSpaceFromLayout message', {
                            layoutId,
                            currentUrl,
                            ar,
                        }),
                    );
                    dispatch(Core.setOpen());
                    dispatch(
                        Core.startSpaceFromLayout({
                            currentUrl,
                            layoutId,
                            ar,
                        }),
                    );
                },
                /**
                 * List the 'development commands' which can be called from the console
                 * in a dev environment
                 *
                 * See Ticket TDC-875 and Dora ADR 002
                 */
                listDevCommands() {
                    parent?.listCommandNames(ConsoleCommands.list());
                },
                executeDevCommand(command: string, ...args: any[]) {
                    ConsoleCommands.call(command, args ?? []);
                },
            },
        });

        connection.promise.then(p => {
            parent = p;

            closeAppActionsR.current = {
                ...closeAppActionsR.current,
                core_close: () => {
                    dispatch(Core.setClosed());
                    p.closeWidget();
                },
            };

            if (p.onCheckout) {
                setProceedToCart(() => (cart: Cart, from: CheckoutLocation) => {
                    p.onCheckout(cart, from);
                });
            }
            setTrackEvent(() => (event: string, properties?: { [key: string]: any }) => {
                p.trackEvent({ event, properties });
            });
            setToggleModal(() => (modal: boolean) => {
                p.toggleModal(modal);
            });
            setUpdateConversionId(() => (conversionId: string) => {
                p.conversionIdChanged(conversionId);
            });
        });
    }, []);

    /**
     *
     * Apply an enhancement to the closeApp function, ensuring the other behaviours don't get lost
     *
     * A string tag is used to determine 'sameness' - if two enhancements with the same tag are
     * passed then only the last one to arrive will be called on close.
     *
     * This function is intended to be used inside a useEffect hook to avoid adding too many items
     */
    const enhanceCloseApp = H.useMemo(
        () => (tag: string, enhancement: () => void) => {
            closeAppActionsR.current[tag] = enhancement;
        },
        [],
    );

    const closeApp = H.useMemo(
        () => () => {
            Object.values(closeAppActionsR.current).forEach(fn => fn());
        },
        [closeAppActionsR.current],
    );

    return {
        view,
        currentUrl,
        closeApp,
        enhanceCloseApp,
        proceedToCart,
        trackEvent,
        toggleModal,
        updateConversionId,
    };
}
